import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useState, type ReactElement } from 'react'
import { useForm } from 'react-hook-form'
import { contractorProfileComplete } from '../../../shared/schemas/contractor'
import { profilPatchSchema, VAT_MODES, type ProfilPatch, type VatMode } from '../../../shared/schemas/profil'
import { api } from '../../api'
import { RequireSession, useSitzung } from '../../auth'
import { Feld } from '../../components/Feld'
import { Knopf } from '../../components/Knopf'
import { Zustand } from '../../components/Zustand'
import { datumZeit } from '../../format'
import { fehlerText } from '../login'

/** `OwnProfile` aus src/worker/services/profil.ts. `address` ist geparstes JSON und kann alles sein. */
export interface MeinProfil {
  id: string
  display_name: string
  legal_name: string | null
  legal_form: string | null
  representative_name: string | null
  address: unknown
  country: string
  vat_mode: VatMode
  vat_id: string | null
  tax_number: string | null
  iban: string | null
  bic: string | null
  account_holder: string | null
  invoice_prefix: string
  verified_at: string | null
  vollstaendig: boolean
}

export interface ProfilSuche {
  contractor?: string
}

interface ProfilFormWerte {
  legal_name: string
  legal_form: string
  representative_name: string
  street: string
  zip: string
  city: string
  address_country: string
  country: string
  vat_mode: VatMode
  vat_id: string
  tax_number: string
  iban: string
  bic: string
  account_holder: string
}

export const VAT_MODE_LABEL: Readonly<Record<VatMode, string>> = {
  kleinunternehmer: 'Kleinunternehmer (§ 19 UStG)',
  regelbesteuert: 'Regelbesteuert (19 % USt)',
  reverse_charge: 'Reverse Charge (§ 13b UStG)',
}

/**
 * Der eine Eintrag aus `contractorProfileComplete`, der nicht diesem Formular gehört: er hängt an
 * `tenants.vat_id`, und `GET /api/me/profil/:contractorId` kennt keinen Auftraggeber. Er wird
 * deshalb aus der Mängelliste genommen und als Hinweis gezeigt; verbindlich ist `profil.fehlt`
 * aus der Antwort des Abrechnungsscreens (Task 85), die der Server mit dem echten Wert rechnet.
 */
export const AUFTRAGGEBER_USTID = 'USt-IdNr. des Auftraggebers'

function alsAdresse(wert: unknown): { street: string; zip: string; city: string; country: string } {
  const objekt = (wert !== null && typeof wert === 'object' ? wert : {}) as Record<string, unknown>
  const feld = (name: string): string => (typeof objekt[name] === 'string' ? (objekt[name] as string) : '')
  return { street: feld('street'), zip: feld('zip'), city: feld('city'), country: feld('country') }
}

function nullWennLeer(wert: string): string | null {
  return wert.trim() === '' ? null : wert.trim()
}

function werteAus(profil: MeinProfil): ProfilFormWerte {
  const adresse = alsAdresse(profil.address)
  return {
    legal_name: profil.legal_name ?? '',
    legal_form: profil.legal_form ?? '',
    representative_name: profil.representative_name ?? '',
    street: adresse.street,
    zip: adresse.zip,
    city: adresse.city,
    address_country: adresse.country === '' ? profil.country : adresse.country,
    country: profil.country,
    vat_mode: profil.vat_mode,
    vat_id: profil.vat_id ?? '',
    tax_number: profil.tax_number ?? '',
    iban: profil.iban ?? '',
    bic: profil.bic ?? '',
    account_holder: profil.account_holder ?? '',
  }
}

/**
 * Der Patch, den dieses Formular schickt: immer alle elf Felder, nie ein Teilstück. `Required<…>`
 * statt `ProfilPatch`, weil `profilPatchSchema` `.partial()` ist — hier fehlt aber keines, und nur
 * mit dieser Zusage lässt sich derselbe Wert unten an `contractorProfileComplete` weiterreichen.
 */
type VollerProfilPatch = Required<ProfilPatch>

/** Das komplette Profil als Patch. `patchOwnProfile` verwirft unveränderte Felder von selbst. */
function zuPatch(werte: ProfilFormWerte): VollerProfilPatch {
  const adresseLeer = [werte.street, werte.zip, werte.city, werte.address_country].every((wert) => wert.trim() === '')
  return {
    legal_name: nullWennLeer(werte.legal_name),
    legal_form: nullWennLeer(werte.legal_form),
    representative_name: nullWennLeer(werte.representative_name),
    address: adresseLeer
      ? null
      : {
          street: werte.street.trim(),
          zip: werte.zip.trim(),
          city: werte.city.trim(),
          country: werte.address_country.trim().toUpperCase(),
        },
    country: werte.country.trim().toUpperCase(),
    vat_mode: werte.vat_mode,
    vat_id: nullWennLeer(werte.vat_id),
    tax_number: nullWennLeer(werte.tax_number),
    iban: nullWennLeer(werte.iban),
    bic: nullWennLeer(werte.bic),
    account_holder: nullWennLeer(werte.account_holder),
  }
}

const FELD_ZU_FORMULAR: Readonly<Record<string, keyof ProfilFormWerte>> = {
  legal_name: 'legal_name',
  legal_form: 'legal_form',
  representative_name: 'representative_name',
  address: 'street',
  'address.street': 'street',
  'address.zip': 'zip',
  'address.city': 'city',
  'address.country': 'address_country',
  country: 'country',
  vat_mode: 'vat_mode',
  vat_id: 'vat_id',
  tax_number: 'tax_number',
  iban: 'iban',
  bic: 'bic',
  account_holder: 'account_holder',
}

/**
 * `null` heißt: zu diesem Pfad gibt es kein Eingabefeld. Das trifft die Regel `.refine(…)` des
 * Schemas, deren Pfad leer ist („Es wurde nichts geändert."). Ein Rückfall auf irgendein Feld
 * schriebe diesen Satz unter eine fremde Beschriftung; solche Meldungen gehen deshalb in den
 * Fehlerbereich über dem Formular.
 */
function feldVonPfad(pfad: ReadonlyArray<string | number>): keyof ProfilFormWerte | null {
  return FELD_ZU_FORMULAR[pfad.join('.')] ?? null
}

function ProfilFormular({ profil }: { profil: MeinProfil }): ReactElement {
  const queryClient = useQueryClient()
  const [gespeichert, setGespeichert] = useState(false)
  const { register, handleSubmit, setError, clearErrors, watch, formState } = useForm<ProfilFormWerte>({
    values: werteAus(profil),
  })

  const speichern = useMutation({
    mutationFn: (patch: ProfilPatch) => api.patch<{ profil: MeinProfil }>(`/api/me/profil/${profil.id}`, patch),
    onSuccess: (antwort) => {
      queryClient.setQueryData(['me', 'profil', profil.id], antwort)
      setGespeichert(true)
    },
  })

  // Genau der Wert, der beim Speichern zum Server ginge — nicht daneben noch einmal von Hand
  // zusammengesetzt, sonst beschreibt die Mängelliste irgendwann etwas anderes als den Patch.
  const patch = zuPatch(watch())
  // Dieselbe Funktion, die submitInvoice vor dem Einreichen aufruft — nicht nachgebaut, importiert.
  const pruefung = contractorProfileComplete(patch, { vat_id: null })
  const fehlt = pruefung.fehlt.filter((eintrag) => eintrag !== AUFTRAGGEBER_USTID)
  const auftraggeberUstIdNoetig = pruefung.fehlt.includes(AUFTRAGGEBER_USTID)

  const absenden = handleSubmit((eingabe) => {
    setGespeichert(false)
    speichern.reset()
    clearErrors()
    const geprueft = profilPatchSchema.safeParse(zuPatch(eingabe))
    if (!geprueft.success) {
      // Jede Beanstandung wird gezeigt, nicht nur die erste: sonst räumt der Nutzer ein Feld auf
      // und bekommt beim nächsten Klick die nächste Meldung, eine nach der anderen.
      const belegt = new Set<string>()
      for (const problem of geprueft.error.issues) {
        const ziel = feldVonPfad(problem.path) ?? 'root'
        if (belegt.has(ziel)) continue
        belegt.add(ziel)
        setError(ziel, { message: problem.message })
      }
      return
    }
    speichern.mutate(geprueft.data)
  })

  const meldung = (name: keyof ProfilFormWerte): string | undefined => formState.errors[name]?.message

  return (
    <>
      <p className="mt-2 text-[var(--ink-muted)]">
        Rechnungsnummernkreis:{' '}
        <span className="zahl">{profil.invoice_prefix}</span>
        {' · '}
        {profil.verified_at === null
          ? 'Dieses Steuerprofil ist noch nicht geprüft; der Plattform-Administrator gibt es frei.'
          : `Geprüft am ${datumZeit(profil.verified_at)}.`}
      </p>

      {/*
        `aria-live` statt `role="status"`: Dieser Block ändert sich bei jeder Eingabe und ist damit
        eine höfliche Ansage, aber kein Statusbereich neben dem einen, den dieser Bildschirm hat —
        „Gespeichert." ist die Antwort des Servers und muss die einzige `role="status"` bleiben,
        sonst hört ein Bildschirmleser zwei Meldungen und weiß nicht, welche die neue ist.

        Gerechnet wird über den aktuellen Stand des Formulars, damit die Liste beim Tippen mitläuft.
        Genau deshalb sagen beide Fassungen des Texts dazu, dass erst das Speichern zählt — sonst
        behauptete der Kasten eine Vollständigkeit, die auf dem Server noch nicht steht.
      */}
      <section aria-live="polite" className="my-8 border border-[var(--rule)] bg-[var(--paper-sunk)] p-4">
        {fehlt.length === 0 ? (
          <p>
            Diese Eingaben enthalten alles, was eine Rechnung braucht. Verbindlich wird das, sobald Sie
            gespeichert haben.
          </p>
        ) : (
          <>
            <h2 id="fehlt-titel">
              Noch nicht vollständig
            </h2>
            <p className="mt-1 text-sm text-[var(--ink-muted)]">
              Solange eines dieser Felder fehlt, lehnt der Server das Einreichen einer Abrechnung ab.
              Geprüft wird hier, was gerade im Formular steht; gezählt wird beim Einreichen der
              gespeicherte Stand.
            </p>
            <ul aria-labelledby="fehlt-titel" className="mt-2 list-disc pl-5">
              {fehlt.map((eintrag) => (
                <li key={eintrag}>{eintrag}</li>
              ))}
            </ul>
          </>
        )}
        {auftraggeberUstIdNoetig && (
          <p className="mt-3 text-sm text-[var(--ink-muted)]">
            Bei Reverse Charge muss zusätzlich die USt-IdNr. Ihres Auftraggebers hinterlegt sein. Das prüft
            der Server beim Einreichen; auf der Abrechnungsseite sehen Sie es je Auftraggeber.
          </p>
        )}
      </section>

      {speichern.error !== null && (
        <p role="alert" className="mb-6 border-l-2 border-[var(--alert)] pl-3 text-[var(--alert)]">
          {fehlerText(speichern.error)}
        </p>
      )}
      {formState.errors.root?.message !== undefined && (
        <p role="alert" className="mb-6 border-l-2 border-[var(--alert)] pl-3 text-[var(--alert)]">
          {formState.errors.root.message}
        </p>
      )}
      {gespeichert && <p role="status" className="mb-6">Gespeichert.</p>}

      <form onSubmit={(ereignis) => void absenden(ereignis)} noValidate className="grid gap-x-6 md:grid-cols-2">
        <div className="md:col-span-2">
          <Feld label="Rechtlicher Name" htmlFor="p-legal-name" fehler={meldung('legal_name')}>
            <input id="p-legal-name" {...register('legal_name')} />
          </Feld>
        </div>
        <Feld label="Rechtsform" htmlFor="p-legal-form" fehler={meldung('legal_form')}>
          <input id="p-legal-form" {...register('legal_form')} />
        </Feld>
        <Feld label="Vertreten durch" htmlFor="p-rep" fehler={meldung('representative_name')}>
          <input id="p-rep" {...register('representative_name')} />
        </Feld>
        <div className="md:col-span-2">
          <Feld label="Straße und Hausnummer" htmlFor="p-street" fehler={meldung('street')}>
            <input id="p-street" {...register('street')} />
          </Feld>
        </div>
        <Feld label="PLZ" htmlFor="p-zip" fehler={meldung('zip')}>
          <input id="p-zip" {...register('zip')} />
        </Feld>
        <Feld label="Ort" htmlFor="p-city" fehler={meldung('city')}>
          <input id="p-city" {...register('city')} />
        </Feld>
        <Feld label="Land der Anschrift" htmlFor="p-address-country" fehler={meldung('address_country')}>
          <input id="p-address-country" maxLength={2} {...register('address_country')} />
        </Feld>
        <Feld label="Sitzland" htmlFor="p-country" fehler={meldung('country')}>
          <input id="p-country" maxLength={2} {...register('country')} />
        </Feld>
        <Feld label="Umsatzsteuer" htmlFor="p-vat-mode" fehler={meldung('vat_mode')}>
          <select id="p-vat-mode" {...register('vat_mode')}>
            {VAT_MODES.map((modus) => (
              <option key={modus} value={modus}>{VAT_MODE_LABEL[modus]}</option>
            ))}
          </select>
        </Feld>
        <Feld label="USt-IdNr." htmlFor="p-vat-id" fehler={meldung('vat_id')}>
          <input id="p-vat-id" {...register('vat_id')} />
        </Feld>
        <Feld label="Steuernummer" htmlFor="p-tax-number" fehler={meldung('tax_number')}>
          <input id="p-tax-number" {...register('tax_number')} />
        </Feld>
        <div className="md:col-span-2">
          <Feld label="IBAN" htmlFor="p-iban" fehler={meldung('iban')}>
            <input id="p-iban" className="zahl" {...register('iban')} />
          </Feld>
        </div>
        <Feld label="BIC" htmlFor="p-bic" fehler={meldung('bic')}>
          <input id="p-bic" {...register('bic')} />
        </Feld>
        <Feld label="Kontoinhaber" htmlFor="p-holder" fehler={meldung('account_holder')}>
          <input id="p-holder" {...register('account_holder')} />
        </Feld>
        <p className="md:col-span-2">
          <Knopf type="submit" disabled={speichern.isPending}>Speichern</Knopf>
          <span className="ml-4 text-sm text-[var(--ink-muted)]">
            Änderungen an Name, Land, Steuerangaben oder IBAN setzen die Prüfung zurück.
          </span>
        </p>
      </form>
    </>
  )
}

export function ProfilScreen({
  contractorId,
  contractors,
  onContractor,
}: {
  contractorId: string | null
  contractors: ReadonlyArray<{ id: string; display_name: string }>
  onContractor: (id: string) => void
}): ReactElement {
  const profil = useQuery({
    queryKey: ['me', 'profil', contractorId ?? ''],
    enabled: contractorId !== null,
    queryFn: () => api.get<{ profil: MeinProfil }>(`/api/me/profil/${contractorId ?? ''}`),
  })

  // Dieselbe Vorsichtsmaßnahme wie im <MandantenWechsler>: nennt die Adresse einen Auftragnehmer,
  // den diese Sitzung nicht führt, zeigte <select> stillschweigend den ersten Eintrag an — auf dem
  // Schirm stünde dann ein Name, während die Abfrage ein anderes Profil holt (und 404 bekommt).
  const bekannt = contractorId !== null && contractors.some((eintrag) => eintrag.id === contractorId)

  return (
    <div className="seite">
      <h1>Mein Profil</h1>

      {contractorId === null && (
        <p role="status" className="mt-6">
          Für diesen Zugang ist kein Auftragnehmer-Profil hinterlegt.
        </p>
      )}

      {contractors.length > 1 && (
        <p className="mt-4">
          <label htmlFor="profil-auswahl" className="block text-sm text-[var(--ink-muted)]">Profil</label>
          <select
            id="profil-auswahl"
            className="mt-1 border border-[var(--rule-strong)] bg-[var(--paper)] px-2 py-1"
            value={bekannt ? contractorId : ''}
            onChange={(ereignis) => onContractor(ereignis.target.value)}
          >
            {!bekannt && (
              <option value="" disabled>
                Bitte wählen …
              </option>
            )}
            {contractors.map((eintrag) => (
              <option key={eintrag.id} value={eintrag.id}>{eintrag.display_name}</option>
            ))}
          </select>
        </p>
      )}

      {/*
        Ein Profil gibt es immer, sobald ein Auftragnehmer gewählt ist — deshalb hat dieser
        Bildschirm nur „lädt" und „Fehler" und keinen leeren Zustand. Der Fall ohne jedes Profil
        steht oben und ist etwas anderes als eine leere Antwort.
      */}
      {profil.isError && (
        <Zustand art="fehler" fehler={profil.error} onWiederholen={() => void profil.refetch()} />
      )}
      {profil.isPending && contractorId !== null && (
        <Zustand art="laedt" beschriftung="Ihr Profil wird geladen." />
      )}
      {/*
        `key`: Beim Wechsel des Auftragnehmers ist das Formular ein anderes. Ohne den Schlüssel
        bliebe „Gespeichert." oder die Fehlermeldung des vorigen Profils über dem neuen stehen.
      */}
      {profil.data !== undefined && <ProfilFormular key={profil.data.profil.id} profil={profil.data.profil} />}
    </div>
  )
}

function ProfilInhalt(): ReactElement {
  const suche = Route.useSearch()
  const navigate = useNavigate()
  const { contractors } = useSitzung()
  const gewaehlt = suche.contractor ?? contractors[0]?.id ?? null
  return (
    <ProfilScreen
      contractorId={gewaehlt}
      contractors={contractors}
      onContractor={(id) => void navigate({ to: '/me/profil', search: { contractor: id } })}
    />
  )
}

/** `useSitzung()` ist nur unterhalb von <RequireSession> zulässig — deshalb steht der Schutz hier. */
function ProfilRoute(): ReactElement {
  return (
    <RequireSession>
      <ProfilInhalt />
    </RequireSession>
  )
}

export const Route = createFileRoute('/me/profil')({
  validateSearch: (roh: Record<string, unknown>): ProfilSuche => ({
    contractor: typeof roh['contractor'] === 'string' && roh['contractor'] !== '' ? roh['contractor'] : undefined,
  }),
  component: ProfilRoute,
})
